<script setup lang="ts">
import { fromNow } from '@thecompaniesapi/date-helpers'

const userStore = useUserStore()
const api = useApi()
const { t } = useI18n()
const localePath = useLocalePath()

const fetchingUsage = ref(false)
const currentPage = useRouteQuery('page', '1', { transform: value => Number(value) })
const perPage = useRouteQuery('perPage', '10', { transform: value => Number(value) })

const perPageOptions = [
  {
    label: '10',
    value: 10,
  },
  {
    label: '25',
    value: 25,
  },
  {
    label: '50',
    value: 50,
  },
  {
    label: '100',
    value: 100,
  },
]

const { data, refresh } = useAsyncData('team-usage', () => fetchUsage())

const meta = computed(() => {
  return data.value?.meta
})

const usage = computed(() => {
  return data.value?.results
})

const columns = [
  {
    class: 'w-48',
    key: 'event',
    label: t('event'),
  },
  {
    class: 'w-96',
    key: 'endpoint',
    label: t('endpoint'),
  },
  {
    class: 'w-96',
    key: 'request',
    label: t('request'),
  },
  {
    class: 'w-24',
    key: 'cost',
    label: t('cost'),
  },
  {
    class: 'w-48',
    key: 'createdAt',
    label: t('createdAt'),
  },
]

async function fetchUsage() {
  fetchingUsage.value = true

  try {
    const teamId = userStore.user?.currentTeamId

    if (!teamId) {
      return
    }

    return await api
      .teams
      .usage
      .fetch({
        page: currentPage.value,
        size: perPage.value,
        teamId,
      })
      .then(result => result.data)
  }
  catch (error) {
    console.error(error)
  }
  finally {
    fetchingUsage.value = false
  }
}

watch([currentPage, perPage], () => refresh())

// Fetch usage on client side only, the request can be slow as it pulls data from PostHog

defineShortcuts({
  arrowleft: () => {
    if (fetchingUsage.value) {
      return
    }

    if (currentPage.value > 1) {
      currentPage.value--
    }
  },
  arrowright: () => {
    if (fetchingUsage.value) {
      return
    }

    if (currentPage.value < (meta.value?.lastPage || 1)) {
      currentPage.value++
    }
  },
})

// Helper to format the request data nicely
function formatRequestData(request: Record<string, any> | undefined) {
  if (!request) {
    return []
  }

  return Object.entries(request)
    .filter(([_, value]) => !!value)
    .map(
      ([key, value]) => ({
        key,
        value: typeof value === 'object' ? JSON.stringify(value, null, 2) : value.toString(),
      }),
    )
}
</script>

<template>
  <div class="w-full">
    <div class="flex flex-col gap-4">
      <UTable
        :columns="columns"
        :loading="fetchingUsage"
        :rows="usage"
        :ui="{
          tbody: '',
          loadingState: {
            wrapper: 'hidden',
          },
          td: {
            color: 'text-gray-900 dark:text-gray-100',
            base: 'whitespace-nowrap',
          },
          th: {
            base: 'text-xs uppercase p-0 whitespace-nowrap',
            font: 'font-normal',
          },
        }"
      >
        <template #event-data="{ row }">
          <div class="flex items-center gap-2 truncate">
            <span class="truncate font-bold">
              {{ t(`events.${row.event}`) }}
            </span>
          </div>
        </template>

        <template #endpoint-data="{ row }">
          <div class="w-full max-w-96 overflow-x-auto">
            <template v-if="mapUrlToDocId(row.endpoint)">
              <UTooltip :text="t('openInDocs')">
                <UButton
                  class="truncate text-sm text-gray-500"
                  size="2xs"
                  :to="localePath({ path: mapUrlToDocId(row.endpoint) })"
                  :ui="{ base: '!pl-1 pr-2' }"
                >
                  <div class="flex items-center gap-2">
                    <UBadge
                      color="green"
                      size="xs"
                      variant="subtle"
                    >
                      {{ row?.method || 'GET' }}
                    </UBadge>

                    <span class="truncate">
                      {{ row.endpoint }}
                    </span>
                  </div>
                </UButton>
              </UTooltip>
            </template>

            <span
              v-else
              class="truncate text-sm text-gray-500"
            >
              {{ row.endpoint }}
            </span>
          </div>
        </template>

        <template #request-data="{ row }">
          <div
            v-if="row.request"
            class="flex flex-col gap-1"
          >
            <div v-if="Object.keys(row.request).length > 0">
              <UPopover
                class="w-fit"
                mode="hover"
                :popper="{ placement: 'bottom-start' }"
              >
                <UButton
                  class="truncate text-sm text-gray-500"
                  :icon="getIcon('api')"
                  :label="`${Object.keys(row.request).length} ${t('parameters', { count: Object.keys(row.request).length })}`"
                  size="2xs"
                  :ui="{ icon: { base: 'text-indigo-500 dark:text-indigo-400' } }"
                />

                <template #panel>
                  <div class="max-h-96 w-96 overflow-y-auto p-2">
                    <div
                      v-for="item in formatRequestData(row.request)"
                      :key="item.key"
                      class="mb-2 flex flex-col gap-1 last:mb-0"
                    >
                      <div class="text-xs font-medium text-gray-500">
                        {{ item.key }}
                      </div>

                      <UCard
                        class="!p-2"
                        :ui="{ base: 'bg-gray-50 dark:bg-gray-900', body: { padding: '!p-1' } }"
                      >
                        <pre class="whitespace-pre-wrap break-all text-xs">{{ item.value }}</pre>
                      </UCard>
                    </div>
                  </div>
                </template>
              </UPopover>
            </div>
          </div>
        </template>

        <template #cost-data="{ row }">
          <div
            v-if="typeof row?.cost === 'number'"
            class="flex select-none justify-end"
          >
            <UTooltip :text="row?.cost === 0 ? t('free') : t('priceNotAvailableTooltip')">
              <BaseCoin
                :credits="row?.cost === 0 ? 0 : row?.cost || t('priceNotAvailable')"
                hide-suffix
                size="sm"
              />
            </UTooltip>
          </div>
        </template>

        <template #createdAt-data="{ row }">
          <UBadge
            v-if="row?.createdAt"
            color="blue"
            variant="subtle"
          >
            {{ fromNow(new Date(row?.createdAt)) }}
          </UBadge>
        </template>
      </UTable>

      <div
        v-if="meta?.lastPage && meta.lastPage > 1"
        class="flex items-center justify-between p-4"
      >
        <UTooltip :text="t('perPage')">
          <USelect
            v-model="perPage"
            :options="perPageOptions"
            size="sm"
            :ui="{
              select: {
                base: 'min-w-[100px]',
              },
            }"
          >
            <template #prefix>
              <span class="text-xs text-gray-500">{{ t('perPage') }}</span>
            </template>
          </USelect>
        </UTooltip>

        <UPagination
          v-model="currentPage"
          :max="5"
          :next-button="{ icon: 'i-heroicons-chevron-right-20-solid', trailing: true }"
          :page-count="perPage"
          :prev-button="{ icon: 'i-heroicons-chevron-left-20-solid' }"
          size="md"
          :total="meta?.total || 0"
          :ui="{ rounded: 'h-[40px]' }"
        />
      </div>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "cost": "Cost",
    "createdAt": "When",
    "endpoint": "Endpoint",
    "event": "Event",
    "events": {
      "api-request": "API Request"
    },
    "free": "Free",
    "openInDocs": "Open in documentation",
    "parameters": "parameter|parameters",
    "perPage": "Per page",
    "priceNotAvailable": "Unavailable",
    "priceNotAvailableTooltip": "The cost of this request is not available.",
    "request": "Request"
  },
  "es": {
    "cost": "Costo",
    "createdAt": "Cuando",
    "endpoint": "Endpoint",
    "event": "Evento",
    "events": {
      "api-request": "Solicitud API"
    },
    "free": "Gratuito",
    "openInDocs": "Abrir en documentación",
    "parameters": "parámetro|parámetros",
    "perPage": "Por página",
    "priceNotAvailable": "Indisponible",
    "priceNotAvailableTooltip": "El costo de esta solicitud no está disponible.",
    "request": "Solicitud"
  },
  "fr": {
    "cost": "Coût",
    "createdAt": "Quand",
    "endpoint": "Endpoint",
    "event": "Événement",
    "events": {
      "api-request": "Requête API"
    },
    "free": "Gratuit",
    "openInDocs": "Ouvrir dans la documentation",
    "parameters": "paramètre|paramètres",
    "perPage": "Par page",
    "priceNotAvailable": "Indisponible",
    "priceNotAvailableTooltip": "Le coût de cette requête n'est pas disponible.",
    "request": "Requête"
  }
}
</i18n>
